// Reusable worker-heartbeat card. Used by:
//   • /admin/fedex      (fedex-ingest-poll)
//   • /admin/tracking   (tracking-poll)
//
// Backed by a per-worker GET /worker-status endpoint that reads from
// the shared worker_runs heartbeat table. The card renders a single-
// line strip with status pill + Last poll / Last success / Last 24h
// totals, plus an inline error/stale banner when relevant.
//
// Each consumer supplies its own queryFn + stats config so the visible
// labels match what that worker actually counts. See call sites for
// concrete shapes.

import { useQuery } from '@tanstack/react-query'
import { Activity, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react'

function fmtRelative(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  if (Number.isNaN(diffMs)) return '—'
  const s = Math.round(diffMs / 1000)
  if (s < 5)     return 'just now'
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

// Visual config per status verdict.
const STATUS_CONFIG = {
  ok:        { dot: 'bg-emerald-500', label: 'Healthy',   cls: 'border-emerald-200 bg-emerald-50/30' },
  stale:     { dot: 'bg-amber-500',   label: 'Stale',     cls: 'border-amber-200 bg-amber-50/40'    },
  error:     { dot: 'bg-red-500',     label: 'Errored',   cls: 'border-red-200 bg-red-50/40'         },
  never_run: { dot: 'bg-slate-400',   label: 'Never run', cls: 'border-slate-200 bg-slate-50/50'     },
  unknown:   { dot: 'bg-slate-300',   label: 'Unknown',   cls: 'border-slate-200 bg-slate-50/50'     },
}

const ACCENT_CLASSES = {
  emerald: 'text-emerald-700 font-medium',
  amber:   'text-amber-700 font-medium',
  red:     'text-red-700 font-medium',
  slate:   'text-slate-500',
}

/**
 * Props:
 *   workerName   — string shown next to the cadence (e.g. 'tracking-poll')
 *   queryKey     — TanStack Query cache key (e.g. ['tracking','worker-status'])
 *   queryFn      — function returning the /worker-status response
 *   statsConfig  — ordered array of { key, label, accent? } describing how
 *                  to render the totals_24h object. accent: 'emerald'|'amber'|'red'|'slate'
 *                  (defaults to 'slate'). The first entry is rendered as a
 *                  primary number (emerald) by default for the most-important stat.
 *   stalePathHint — string like 'DigitalOcean → Workers → tracking-poll'
 *                  shown in the stale-state banner. Optional.
 */
export default function WorkerStatusCard({
  workerName,
  queryKey,
  queryFn,
  statsConfig,
  stalePathHint,
}) {
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  if (isLoading || !data) {
    return (
      <div className="card px-4 py-3 flex items-center gap-3 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Loading worker status…
      </div>
    )
  }

  const c = STATUS_CONFIG[data.status] || STATUS_CONFIG.unknown
  const last = data.last_run
  const lastSuccess = data.last_success
  const intervalMin = Math.round((data.poll_interval_seconds || 900) / 60)
  const totals = data.totals_24h || {}

  return (
    <div className={`card border ${c.cls}`}>
      <div className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {/* Status pill */}
        <div className="flex items-center gap-2 min-w-[160px]">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`} />
          <div>
            <div className="font-semibold text-slate-800 leading-tight flex items-center gap-1.5">
              <Activity size={14} />
              Worker: {c.label}
            </div>
            <div className="text-xs text-slate-500 leading-tight">
              {workerName} · every {intervalMin}m
            </div>
          </div>
        </div>

        {/* Last poll */}
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last poll</div>
          <div className="text-slate-700">
            {last ? fmtRelative(last.started_at) : '—'}
            {last?.status === 'running' && (
              <span className="ml-1.5 text-xs text-slate-400 italic">in progress</span>
            )}
          </div>
        </div>

        {/* Last success */}
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last success</div>
          <div className="text-slate-700">
            {lastSuccess ? fmtRelative(lastSuccess.completed_at || lastSuccess.started_at) : '—'}
          </div>
        </div>

        {/* 24h totals — driven by per-worker statsConfig */}
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last 24h</div>
          <div className="text-slate-700 tabular-nums">
            {statsConfig.map((sc, i) => {
              const value = totals[sc.key] ?? 0
              const accent = sc.accent || (i === 0 ? 'emerald' : 'slate')
              return (
                <span key={sc.key}>
                  {i > 0 && <span className="text-slate-400 mx-1">·</span>}
                  <span className={ACCENT_CLASSES[accent] || ACCENT_CLASSES.slate}>{value}</span>
                  <span className="text-slate-400 ml-1">{sc.label}</span>
                </span>
              )
            })}
            {(totals.errors || 0) > 0 && (
              <>
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-red-700 font-medium">{totals.errors} err</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Inline banners — only shown when relevant */}
      {data.status === 'error' && last?.error_message && (
        <div className="px-4 py-2.5 border-t border-red-200 bg-red-50/60 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Last error:</span> {last.error_message}
          </div>
        </div>
      )}
      {data.status === 'stale' && (
        <div className="px-4 py-2.5 border-t border-amber-200 bg-amber-50/60 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            No poll in over {Math.round((data.poll_interval_seconds || 900) * 2 / 60)} minutes —
            the worker may be stopped.{stalePathHint && ` Check ${stalePathHint}.`}
          </div>
        </div>
      )}
      {data.status === 'unknown' && (
        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div>{data.note || 'Heartbeat table not present. Run the 2026-05-17-worker-runs migration.'}</div>
        </div>
      )}
    </div>
  )
}
